import { getApiConfig } from "../utils/config";
import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";

let _tokenFetcher: () => Promise<string>;

const createAuthApiService = () => {
  const authApiService = axios.create({
    baseURL: getApiConfig().authApiUrl,
    headers: {
      "Content-Type": "application/json",
    },
  });

  authApiService.interceptors.request.use(
    async (config: AxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      config.headers = config.headers || {};

      if (!_tokenFetcher) {
        return Promise.resolve({
          ...config,
          // eslint-disable-next-line import/no-named-as-default-member
          cancelToken: new axios.CancelToken((cancel: (reason?: string) => void) =>
            cancel(
              "Auth API was not initialized with the appropriate token fetching function."
            )
          ),
        } as unknown as InternalAxiosRequestConfig);
      }

      try {
        const jwt = await _tokenFetcher();
        return Promise.resolve({
          ...config,
          headers: { ...config.headers, Authorization: `Bearer ${jwt}` },
        } as unknown as InternalAxiosRequestConfig);
      } catch (e) {
        return Promise.resolve({
          ...config,
          // eslint-disable-next-line import/no-named-as-default-member
          cancelToken: new axios.CancelToken((cancel: (reason?: string) => void) =>
            cancel("No access token!")
          ),
        } as unknown as InternalAxiosRequestConfig);
      }
    },
    (error: Error) => Promise.reject(error)
  );

  authApiService.interceptors.response.use(
    (response: any) => {
      return response;
    },
    (error: any) => {
      return Promise.reject(error.response?.data || error.response);
    }
  );

  return authApiService;
};

const authApiService = createAuthApiService();

// Auth0-specific API endpoints that should use the public API
export const authService = {
  // Get user profile from Auth0
  getUserProfile: (userId: string) => {
    return authApiService.get(`/api/v2/users/${userId}`);
  },

  // Update user metadata
  updateUserMetadata: (userId: string, metadata: any) => {
    return authApiService.patch(`/api/v2/users/${userId}`, {
      user_metadata: metadata
    });
  },

  // Get user roles
  getUserRoles: (userId: string) => {
    return authApiService.get(`/api/v2/users/${userId}/roles`);
  },

  // Assign roles to user
  assignUserRoles: (userId: string, roleIds: string[]) => {
    return authApiService.post(`/api/v2/users/${userId}/roles`, {
      roles: roleIds
    });
  },

  // Remove roles from user
  removeUserRoles: (userId: string, roleIds: string[]) => {
    return authApiService.delete(`/api/v2/users/${userId}/roles`, {
      data: { roles: roleIds }
    });
  },

  // Get all roles
  getRoles: () => {
    return authApiService.get('/api/v2/roles');
  },

  // Create a new role
  createRole: (roleData: any) => {
    return authApiService.post('/api/v2/roles', roleData);
  },

  // Update role
  updateRole: (roleId: string, roleData: any) => {
    return authApiService.patch(`/api/v2/roles/${roleId}`, roleData);
  },

  // Delete role
  deleteRole: (roleId: string) => {
    return authApiService.delete(`/api/v2/roles/${roleId}`);
  },

  // Get permissions
  getPermissions: () => {
    return authApiService.get('/api/v2/permissions');
  },

  // Assign permissions to role
  assignPermissionsToRole: (roleId: string, permissionIds: string[]) => {
    return authApiService.post(`/api/v2/roles/${roleId}/permissions`, {
      permissions: permissionIds
    });
  },

  // Remove permissions from role
  removePermissionsFromRole: (roleId: string, permissionIds: string[]) => {
    return authApiService.delete(`/api/v2/roles/${roleId}/permissions`, {
      data: { permissions: permissionIds }
    });
  }
};

/**
 * Provide the ability to pass the getAccessTokenSilently function from Auth0 when the app bootstraps.
 * This function should be called with the same token fetcher used for the main API service.
 * @param tokenFetcher
 */
export const setAuthTokenFetcher = (tokenFetcher: () => Promise<string>) => {
  _tokenFetcher = tokenFetcher;
  return this;
};

export default authService; 